import { HTMLAttributes, FC, useCallback, useMemo } from "react";
import clsx from "classnames";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { UserSubscriptionStatus } from "@onmo/graphql/graphql";
import { login, selectIsAuthenticated } from "@onmo/auth";
import { TypographyVariant } from "../../../constants/typography";
import { Typography } from "../../core/Typography/Typography";
import { Icon } from "../../core/Icon/Icon";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import Console from "../../../assets/console/console-image.webp";
import Console2x from "../../../assets/console/console-image@2x.webp";
import Console3x from "../../../assets/console/console-image@3x.webp";
import ConsoleInactive from "../../../assets/console/console-image-inactive.webp";
import ConsoleInactive2x from "../../../assets/console/console-image-inactive@2x.webp";
import ConsoleInactive3x from "../../../assets/console/console-image-inactive@3x.webp";
import Logo from "../../../assets/icons/logo_onmo+_1x.webp";
import Logo2x from "../../../assets/icons/logo_onmo+_2x.webp";
import Logo3x from "../../../assets/icons/logo_onmo+_3x.webp";
import { useAppSelector } from "../../hooks";
import { useStyles } from "./ConsoleCardStatus.style";
import {
  hasStatus,
  isConsoleActive,
  noLink,
  showMoreSpaceConsoleCard,
  getStatusInfo,
} from "../../../models/user";
import { getDayLeft } from "../../../models/time";
import { PageRoutes } from "../../../constants/routes";
import { SettingsView } from "../../../constants/settings";
import { AnalyticsGA4 } from "../../../models/analyticsGA4";
import { useSubscriptionNavigate } from "../../../hooks/useSubscriptionNavigate";
import { useAAASubscription } from "../../../hooks/useAAASubscription";
import { useTranslation } from "react-i18next";
import { isSubscriptionDisabled } from "../../../utils/platform";

interface ConsoleCardStatusProps extends HTMLAttributes<HTMLDivElement> {
  setActivateQRLink?: (link: string) => void;
}

export const ConsoleCardStatus: FC<ConsoleCardStatusProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { className, setActivateQRLink, ...othersProps } = props;
  const navigateSubscription = useSubscriptionNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { data: dataSubscription } = useAAASubscription({
    skip: !isAuthenticated,
  });
  const status = dataSubscription?.me?.subscription?.status;
  const isRecurring = Boolean(dataSubscription?.me?.subscription?.isRecurring);
  const validUntil = dataSubscription?.me?.subscription?.validUntil;
  const statusInfo = getStatusInfo(status, validUntil, isRecurring);
  const linkAccess = useMemo(() => {
    if (
      status === UserSubscriptionStatus.Subscribed &&
      validUntil &&
      getDayLeft(validUntil) < 30 &&
      !isRecurring
    ) {
      return {
        text: t("Console_Renew Now"),
        actions: () =>
          navigateSubscription(PageRoutes.Paywall, {
            state: { fromTo: "console" },
          }),
      };
    } else if (status === UserSubscriptionStatus.NotSubscribed) {
      return {
        text: t("Console_Activate"),
        actions: () =>
          navigateSubscription(PageRoutes.Paywall, {
            state: { fromTo: "console" },
          }),
      };
    } else if (!isAuthenticated) {
      return {
        text: t("Console_Login"),
        actions: login,
      };
    } else if (
      status === UserSubscriptionStatus.Expired ||
      status === UserSubscriptionStatus.Grace
    ) {
      return {
        text: t("Console_Renew Now"),
        actions: () =>
          navigateSubscription(PageRoutes.Paywall, {
            state: { fromTo: "console" },
          }),
      };
    } else {
      return {
        text: t("Console_Active"),
        actions: () => {
          if (!isSubscriptionDisabled) {
            navigateSubscription(PageRoutes.Settings, {
              state: { settingsView: SettingsView.Subscription },
            });
          }
        },
      };
    }
  }, [
    status,
    validUntil,
    isRecurring,
    isAuthenticated,
    navigateSubscription,
    t,
  ]);

  const handleAction = () => {
    AnalyticsGA4.homepageAction("console");
    linkAccess?.actions?.();
  };

  return (
    <div
      {...othersProps}
      className={clsx(classes.root, className)}
      onClick={handleAction}
    >
      <div
        className={classes.content}
        data-gamepad="console-card"
        data-selected-class={classes.selected}
      >
        <div className={classes.container}>
          <div className={classes.mainLogo}>
            <StaticImage src={Logo} src2x={Logo2x} src3x={Logo3x} />
            <Typography variant={TypographyVariant.HeadingH5Bold}>
              {t("Console_Smart Console")}
            </Typography>
          </div>
          {hasStatus(status) && (
            <div className={classes.item}>
              <div className={clsx(classes.activeDot, statusInfo)} />
              <Typography
                variant={TypographyVariant.BodyB5Medium}
                className={classes.status}
              >
                {statusInfo === "subscribed" && t("Console_Active status")}
                {statusInfo === "expired" && t("Console_Expired")}
                {statusInfo === "expire-soon" &&
                  t("Console_Your subscription expires in days", {
                    count: getDayLeft(validUntil),
                  })}
                {statusInfo === "grace" &&
                  t("Console_Your subscription expires in 1 day")}
              </Typography>
            </div>
          )}
          {!noLink(status, validUntil, isRecurring) && (
            <div className={classes.action}>
              <Typography variant={TypographyVariant.BodyB4Medium}>
                {linkAccess?.text}
              </Typography>
              <Icon name="icon-linear-arrow-right" size={20} />
            </div>
          )}
        </div>
        <StaticImage
          src={isConsoleActive(status, validUntil) ? Console : ConsoleInactive}
          src2x={
            isConsoleActive(status, validUntil) ? Console2x : ConsoleInactive2x
          }
          src3x={
            isConsoleActive(status, validUntil) ? Console3x : ConsoleInactive3x
          }
          className={clsx(
            classes.console,
            showMoreSpaceConsoleCard(status, validUntil) && "moreSpace",
          )}
        />
      </div>
    </div>
  );
};
